/**
 * Utility functions to check if evaluation cards have data to display
 *
 * Each function takes an evaluation object and returns a boolean indicating
 * whether that specific card has meaningful data to show to the user.
 *
 * These functions are used by parent components to conditionally render cards
 * when the "Display non-empty cards only" toggle is enabled.
 *
 * @param {Evaluation} evaluation - The evaluation response to check
 * @returns {boolean} True if the current card has data; false if it has no data
 */

import type { Evaluation } from '~/entities/types';

/**
 * Check if Returned Reason Codes card has data
 */
export const hasReturnedReasonCodesData = (evaluation: Evaluation): boolean => {
  const taggedResults = evaluation.tagged_reason_code_results || {};
  const untaggedResults = evaluation.untagged_reason_code_results || {};
  const extraneousResults = evaluation.extraneous_reason_code_results || {};

  return (
    Object.keys(taggedResults).length > 0
    || Object.keys(untaggedResults).length > 0
    || Object.keys(extraneousResults).length > 0
  );
};

/**
 * Check if Old Returned Reason Codes card has data (pre-cutoff evaluations)
 */
export const hasOldReturnedReasonCodesData = (evaluation: Evaluation): boolean => {
  const reasonCodes = evaluation.reason_codes_results || {};
  const extraneousCodes = evaluation.extraneous_reason_codes || {};

  return (
    Object.keys(reasonCodes).length > 0
    || Object.keys(extraneousCodes).length > 0
  );
};

/**
 * Check if Data Sources card has data
 */
export const hasDataSourcesData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.summary?.services
  && Object.keys(evaluation.final_response.summary.services).length > 0
);

/**
 * Check if Device Details card has data
 */
export const hasDeviceDetailsData = (evaluation: Evaluation): boolean => !!(
  evaluation.device_details
  && Array.isArray(evaluation.device_details)
  && evaluation.device_details.length > 0
);

/**
 * Check if Custom Fields card has data
 */
export const hasCustomFieldsData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.summary?.custom_fields
  && Object.keys(evaluation.final_response.summary.custom_fields).length > 0
);

/**
 * Check if Imported Data card has data
 */
export const hasImportedData = (evaluation: Evaluation): boolean => (
  (evaluation.imported_outcome_reasons?.length || 0) > 0
  || (evaluation.imported_output_attributes?.length || 0) > 0
  || (evaluation.imported_matrix_models?.length || 0) > 0
);

/**
 * Check if Matrix Model card has data
 *
 * NOTE: CRA evaluations filter out matrix models where is_risk_score_factor=true.
 * This basic check doesn't account for that filtering. When integrating with
 * EvaluationOverviewContent, we should use the already-computed matrixModelsToDisplay
 * value instead of calling this function directly.
 *
 * TODO: Remove this note once CRA flag is deprecated.
 */
export const hasMatrixModelData = (evaluation: Evaluation): boolean => {
  const matrixModels = evaluation.final_response?.summary?.matrix_models;
  return !!(matrixModels && Object.keys(matrixModels).length > 0);
};

/**
 * Check if Fraud Risk card has data
 */
export const hasFraudRiskData = (evaluation: Evaluation): boolean => !!(
  evaluation.alloy_fraud_score !== null
  && evaluation.alloy_fraud_score_response
  && (
    evaluation.alloy_fraud_score_response.fraud_score != null
    || evaluation.alloy_fraud_score_response.feature_importance
  )
);

/**
 * Check if Diligence card has data
 */
export const hasDiligenceData = (evaluation: Evaluation): boolean => {
  const diligence = evaluation.final_response?.diligence;
  return !!(diligence && Object.keys(diligence).length > 0);
};

/**
 * Check if Found Data card has data
 */
export const hasFoundData = (evaluation: Evaluation): boolean => {
  const foundData = evaluation.final_response?.found_data;
  return !!(foundData && Object.keys(foundData).length > 0);
};

/**
 * Check if Credit Panel card has data
 */
export const hasCreditPanelData = (evaluation: Evaluation): boolean => {
  const creditData = evaluation.final_response?.diligence?.financial?.credit || [];
  return creditData.filter(c => !!(c.source)).length > 0;
};

/**
 * Check if Evaluation Notes card has data
 */
export const hasEvaluationNotes = (evaluation: Evaluation): boolean => (
  (evaluation.notes?.length || 0) > 0
);

/**
 * Check if Entity Notes card has data
 */
export const hasEntityNotes = (entityNotes: any): boolean => !!(
  entityNotes && Array.isArray(entityNotes) && entityNotes.length > 0
);

/**
 * Check if Matched Data card has data
 */
export const hasMatchedData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.matching
  && Object.keys(evaluation.final_response.matching).length > 0
);

/**
 * Check if Validation Errors card should show
 */
export const hasValidationErrors = (evaluation: Evaluation): boolean => !!(
  evaluation.required && Object.keys(evaluation.required).length > 0
);

/**
 * Check if Webhook Pending card should show
 */
export const hasWebhookPendingData = (evaluation: Evaluation): boolean => !!(
  evaluation.webhook_pending_services
  && Object.keys(evaluation.webhook_pending_services).length > 0
);

/**
 * Check if Partial Details (warnings) card should show
 */
export const hasPartialDetails = (evaluation: Evaluation): boolean => !!(
  evaluation.result === 'partial'
  && evaluation.final_response?.warnings
);

